import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import PrivacyShieldIcon from "../../components/icons/PrivacyShieldIcon";
import { useStoreConsent } from "../../redux/flows/storeConsentFlow";
import { RootState } from "../../redux/store";
import routes from "../../routes/routes";
import { Button } from "components/ui/button";
import TenantLogo from "../layout/components/TenantLogo";
import Header from "../layout/components/Header";
import {
  Select as UiSelect,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "components/ui/select";
import { isSharedDeployment } from "../../util";

// Alphabetically sorted list of tenants (static order)
const TENANTS = [
  "badesee-petershagen",
  "demo",
  "dhu",
  "kolula",
  "kolula-key",
  "neuland",
  "seezeit",
  "strandbad-pertisau",
  "vonovia",
  "segmenta",
];

const DEFAULT_TENANT = "kolula";

function getKeyWithDataProtectionInLowercase(
  obj: Record<string, any>
): string | undefined {
  for (const key in obj) {
    if (key.toLowerCase().includes("dataprotection")) {
      return key;
    }
  }
  return undefined;
}

const ConsentPage = () => {
  const { t, i18n } = useTranslation();
  const hintPages = useSelector((state: RootState) => state.hints.pages);
  const tenantConfig = useSelector(
    (state: RootState) => state.tenantConfig.config
  );
  const storeConsent = useStoreConsent(true);
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const redirectURL = queryParams.get("redirect") || "/";

  // Prefer the tenant config hints URL: DialogComponent matches h.url case-sensitively
  // against the URL segment, and tenant configs use PascalCase (e.g. "DataProtectionPage").
  // Fall back to the legacy redux-keyed lookup for tenants that still rely on the API config.
  let dataProtectionPath = "/hints";
  const configHint = tenantConfig?.content?.[i18n.language]?.hints?.find((h) =>
    h.url?.toLowerCase().includes("dataprotection")
  );
  if (configHint) {
    dataProtectionPath = routes.makePrivacyGDPRPath(configHint.url);
  } else if (hintPages) {
    const path = getKeyWithDataProtectionInLowercase(hintPages);
    if (path) {
      dataProtectionPath = routes.makePrivacyGDPRPath(path);
    }
  }

  const onAcceptConsent = () => {
    storeConsent();
    navigate(redirectURL);
  };

  const [tenantSelected, setTenantSelected] = useState<string>(() => localStorage.getItem("tenant") || DEFAULT_TENANT);

  const environment = process.env.REACT_APP_ENV ?? "integration";

  // The dev tenant switcher only makes sense when a tenant is pinned at build
  // time (REACT_APP_TENANT). On the shared deployment the tenant comes from the
  // URL slug (even though REACT_APP_TENANT may be baked into the build), so the
  // switcher is meaningless and must not appear.
  const showTenantSwitcher =
    !isSharedDeployment() &&
    environment === "integration" &&
    Boolean(process.env.REACT_APP_TENANT);

  const handleTenantSwitch = (tenant: string) => {
    setTenantSelected(tenant);
    localStorage.setItem("tenant", tenant);
    window.location.href = window.location.origin;
  };

  return (
    <div className="flex flex-col items-center px-5">
      <Header>
        <TenantLogo />
      </Header>
      {showTenantSwitcher && (
        <div className="w-full max-w-md my-2">
          <UiSelect value={tenantSelected} onValueChange={handleTenantSwitch}>
            <SelectTrigger aria-label="Tenant">
              <SelectValue placeholder="Select tenant" />
            </SelectTrigger>
            <SelectContent>
              {TENANTS.map((tenant) => (
                <SelectItem key={tenant} value={tenant}>
                  {tenant}
                </SelectItem>
              ))}
            </SelectContent>
          </UiSelect>
        </div>
      )}
      <h1 className="mt-2 text-2xl font-semibold -tracking-[0.0225rem]">
        {t("consent.title")}
      </h1>
      <div className="rounded-full bg-primary/15 p-4 mt-5 mb-5">
        <PrivacyShieldIcon />
      </div>
      <p className="text-center text-lg leading-7 mb-12">
        <Trans
          i18nKey="consent.description"
          components={{
            a: (
              <RouterLink
                className="text-primary underline underline-offset-2 brightness-75"
                to={dataProtectionPath}
              ></RouterLink>
            ),
          }}
        />
      </p>

      <Button
        size="lg"
        variant="primary"
        className="w-full"
        onClick={onAcceptConsent}
      >
        {t("consent.continue")}
      </Button>
    </div>
  );
};

export default ConsentPage;